import { memo, useRef } from "react"
import { noop } from "lib/noop"
import { CircularProgress, Stack } from "@mui/material"
import { useParams } from "react-router"
import "lib/@hooks/useEvent"
import { getDraftAmendsByReferenceIdAndType } from "authoring/routes/project/amends/controller/get-draft-amends-by-reference-id-and-type"
import { AuthoringAmendsType } from "../../../lib/enum/authoring-amends-type.mjs"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { getScheduleDraft } from "./controller/get-schedule-draft"
import { NoResults } from "lib/@components/no-results/no-results"
import { Repeat } from "lib/@components/repeat"
import { AuthoringAuthorNotesCycle } from "./authoring-author-notes-cycle"
import { buildAuthoringAuthorNotesView } from "./lib/authoring-author-notes-data"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { AmendsChanged, DraftReloaded, DraftEdited, SaveSucceeded } from "./schedule-editor-event-definitions.mjs"
import { isSame } from "library/normalized-stringify/index.mjs"

const TAB_MIN_HEIGHT = 302
const COMPARISON_SYNC_DELAY_MS = 800

export const AuthoringAuthorNotesTab = memo(function AuthoringAuthorNotesTab() {
    const { projectId, scheduleId } = useParams()
    const draftId = createDraftId(projectId, scheduleId)
    const comparisonRefreshTimerRef = useRef(null)
    // withRefetch returns undefined when the query resolves to a falsy value (e.g. no draft yet),
    // so default to a [value, refresh] tuple to keep the destructure and the no-draft render safe.
    const [draftData, refreshDraftData] = getScheduleDraft.useResultsOnce.withRefetch(
        { projectId, scheduleId },
        withInitialValue(IS_LOADING)
    ) ?? [undefined, noop]
    const [amends, refreshAmends] = getDraftAmendsByReferenceIdAndType.useResultsOnce.withRefetch(
        draftId,
        AuthoringAmendsType.AUTHORING_AMENDS,
        undefined,
        projectId,
        withInitialValue(IS_LOADING)
    ) ?? [undefined, noop]

    AmendsChanged(draftId).useEvent(refetchAuthorNotesAmendsIfChanged)
    DraftReloaded(draftId).useEvent(refetchAuthorNotesDraftAndAmends)
    DraftEdited(draftId).useEvent(scheduleAuthorNotesComparisonRefresh)
    SaveSucceeded(draftId).useEvent(scheduleAuthorNotesPostSaveRefresh)

    if (!draftId || !projectId) return null
    if (draftData === IS_LOADING || amends === IS_LOADING) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", minHeight: TAB_MIN_HEIGHT }}>
                <CircularProgress />
            </Stack>
        )
    }

    const currentReviewCycle = Math.max(draftData?.reviewCycle ?? 1, getLatestReviewCycle(amends ?? []))
    const { cycleGroups } = buildAuthoringAuthorNotesView({
        records: amends ?? [],
        currentReviewCycle,
        scheduleId,
        draftTasks: draftData?.draft?.tasks ?? [],
    })

    const visibleCycles = cycleGroups.filter(
        (cycle) => cycle.entries.length > 0 || Boolean(cycle.rejection) || Boolean(cycle.returnedToEditing)
    )
    const defaultExpandedCycle = visibleCycles[0]?.reviewCycle ?? null
    const cyclesWithCurrentFlag = visibleCycles.map((cycle) => ({
        ...cycle,
        isCurrent: cycle.reviewCycle === defaultExpandedCycle,
    }))
    const hasVisibleContent = cyclesWithCurrentFlag.length > 0

    return (
        <If truthy={hasVisibleContent}>
            <FitInRemainingSpace setHeight leaveSpace={32}>
                <Stack spacing={2}>
                    <Repeat list={cyclesWithCurrentFlag} item={<AuthoringAuthorNotesCycle />} />
                </Stack>
            </FitInRemainingSpace>
            <Else>
                <NoResults />
            </Else>
        </If>
    )

    function refetchAuthorNotesDraftAndAmends() {
        refreshDraftData()
        refreshAmends()
    }

    function refetchAuthorNotesAmendsIfChanged() {
        void loadAuthorNotesAmendsIfChanged()
    }

    async function loadAuthorNotesAmendsIfChanged() {
        const freshAmends = await getDraftAmendsByReferenceIdAndType(
            draftId,
            AuthoringAmendsType.AUTHORING_AMENDS,
            undefined,
            projectId
        )
        if (isSame(freshAmends ?? [], amends ?? [], { invalidChars: [] })) return
        refreshAmends()
        refreshDraftData()
    }

    function scheduleAuthorNotesComparisonRefresh() {
        if (comparisonRefreshTimerRef.current != null) {
            clearTimeout(comparisonRefreshTimerRef.current)
        }
        comparisonRefreshTimerRef.current = setTimeout(refetchAuthorNotesDraftAndAmends, COMPARISON_SYNC_DELAY_MS)
    }

    function scheduleAuthorNotesPostSaveRefresh() {
        if (comparisonRefreshTimerRef.current != null) {
            clearTimeout(comparisonRefreshTimerRef.current)
        }
        comparisonRefreshTimerRef.current = setTimeout(refetchAuthorNotesDraftAndAmends, COMPARISON_SYNC_DELAY_MS)
    }
})

function getLatestReviewCycle(amends = []) {
    const reviewCycles = amends
        .map((amend) => Number(amend?.payload?.reviewCycle ?? amend?.referenceId?.split("!").at(1)))
        .filter(Number.isInteger)

    return reviewCycles.length > 0 ? Math.max(...reviewCycles) : 1
}
